import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAllPeriodos, getPeriodoById, savePeriodo } from "@/services/periodoLetivo";
import { Button as AntdButton, message } from "antd";
import { Input } from "@/components/ui/inputForm";
import { Table, Switch, Space, ConfigProvider, theme } from "antd";
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { usePermissions } from "@/hooks/usePermissions";

dayjs.locale("pt-br");

export default function ConsultaPeriodoLetivo() {
  const [periodosLetivos, setPeriodosLetivos] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { canInsert, canEdit } = usePermissions();

  useEffect(() => {
    handleGetPeriodosLetivos();
  }, []);
  
  const handleGetPeriodosLetivos = async () => {
    const { data } = await getAllPeriodos();
    const sortedData = data
      .map((p: any) => ({ ...p, ativo: p.ativo ?? true }))
      .sort((a: any, b: any) => {
        // Primeiro compara por data de início
        const compareDataInicio = new Date(b.dataInicio).getTime() - new Date(a.dataInicio).getTime();
        // Se as datas de início forem iguais, compara por data fim
        if (compareDataInicio === 0) {
          return new Date(b.dataFim).getTime() - new Date(a.dataFim).getTime();
        }
        return compareDataInicio;
      });
    setPeriodosLetivos(sortedData);
  };

  const handleAdd = useCallback(() => {
    navigate("/periodoletivo/cadastro");
  }, [navigate]);

  const handleEdit = (id: string) => {
    navigate(`/periodoletivo/${id}`);
  };

  const toggleAtivo = async (id: string) => {
    try {
      const { data: periodoLetivo } = await getPeriodoById(id);
      const updatedPeriodoLetivo = { ...periodoLetivo, ativo: !periodoLetivo.ativo };
      const response = await savePeriodo(updatedPeriodoLetivo);
      
      if (response.status === 200 || response.status === 201) {
        setPeriodosLetivos((prev) =>
          prev.map((p) => (p.id === id ? { ...p, ativo: !p.ativo } : p))
        );
        message.success(`Período Letivo ${!periodoLetivo.ativo ? 'ativado' : 'inativado'} com sucesso!`);
      } else {
        message.error("Falha ao atualizar o status.");
      }
    } catch (error) {
      message.error("Erro ao comunicar com o servidor.");
    }
  };

  const columns = [
    { title: "Nome", dataIndex: "nome", key: "nome" },
    { 
      title: "Data Início", 
      dataIndex: "dataInicio", 
      key: "dataInicio",
      render: (date: string) => dayjs(date).format("DD/MM/YYYY")
    },
    { 
      title: "Data Fim", 
      dataIndex: "dataFim", 
      key: "dataFim",
      render: (date: string) => dayjs(date).format("DD/MM/YYYY")
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (value: boolean, record: any) => (
        <Switch 
          checked={!!value} 
          onChange={() => toggleAtivo(record.id)} 
          checkedChildren="Ativo" 
          unCheckedChildren="Inativo"
        />
      ),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_: any, record: any) => (
        <Space size="middle">
          {canEdit(10) && (
            <AntdButton type="text" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
          )}
        </Space>
      ),
    },
  ];

  const filteredPeriodosLetivos = periodosLetivos.filter((p) =>
    p.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Períodos Letivos</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-end mb-8">
          <Input
            type="text"
            placeholder="Pesquisar por nome"
            className="w-80"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {canInsert(10) && (
            <AntdButton className="flex gap-2" onClick={handleAdd}>
              Novo
              <PlusOutlined />
            </AntdButton>
          )}
        </div>
        <Table columns={columns} dataSource={filteredPeriodosLetivos} rowKey="id" />
      </ConfigProvider>
    </>
  );
}